import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import { AuthProvider } from './auth/auth-provider';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import { createQueryClient } from './lib/query-client';

import './index.css';

// The Fleet API (PR-D #1) does not exist yet. Until it lands, the admin UI
// runs against the MSW mock layer. Disable by setting VITE_USE_MOCKS=false.
const useMocks = (import.meta.env.VITE_USE_MOCKS as string | undefined) !== 'false';

async function bootstrap(): Promise<void> {
  if (useMocks) {
    const [{ startMockWorker }, { seedMockJwt }] = await Promise.all([
      import('./mocks/browser'),
      import('./mocks/dev-auth'),
    ]);

    seedMockJwt();
    await startMockWorker();
  }

  const root = document.getElementById('root');

  if (!root) throw new Error('Root element #root not found');

  const queryClient = createQueryClient();

  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider delayDuration={200}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
            <Toaster />
          </TooltipProvider>
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </StrictMode>,
  );
}

function renderBootstrapError(cause: unknown): void {
  // eslint-disable-next-line no-console
  console.error('Admin UI failed to start', cause);

  const root = document.getElementById('root');

  if (!root) return;

  root.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.setAttribute(
    'style',
    'font-family:system-ui,sans-serif;max-width:32rem;margin:4rem auto;padding:0 1rem;text-align:center;color:#1f2937',
  );

  const heading = document.createElement('h1');
  heading.style.fontSize = '1.125rem';
  heading.style.fontWeight = '600';
  heading.textContent = "Couldn't start the admin console.";

  const detail = document.createElement('p');
  detail.style.marginTop = '0.5rem';
  detail.style.fontSize = '0.875rem';
  detail.style.color = '#6b7280';
  detail.textContent = 'A startup error occurred. Try reloading the page.';

  wrap.append(heading, detail);
  root.append(wrap);
}

void bootstrap().catch(renderBootstrapError);
